import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import "../css/LocationHistorySMS.css";
import { useNavigate } from "react-router-dom";
import { BASE_URL } from "../config";
import { getAuthHeaders } from "../services/useAuth";
import Loader from "../dist/Loader";
import { FaSearch } from "react-icons/fa";

const LocationHistorySMS = () => {
  const navigate = useNavigate();

  // State for dropdown options
  const [states, setStates] = useState([]);
  const [divisions, setDivisions] = useState([]);

  // State for selected filters
  const [selectedState, setSelectedState] = useState("ALL");
  const [selectedDivision, setSelectedDivision] = useState("ALL");
  const [selectedCallType, setSelectedCallType] = useState("ALL");

  // State for table data and UI
  const [tableData, setTableData] = useState([]);
  const [showContainers, setShowContainers] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Trace Date setup
  const [traceDate, setTraceDate] = useState(new Date().toISOString().split('T')[0]);

  // Pagination and Search state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const inputRef = useRef(null);

  const handleExpandSearch = () => {
    setIsSearchExpanded(true);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  // Helper function to format date for display in the table
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      const options = { day: '2-digit', month: 'short', year: 'numeric', weekday: 'long' };
      return date.toLocaleDateString('en-GB', options);
    } catch (e) {
      console.error("Error formatting date:", dateString, e);
      return dateString;
    }
  };

  // Effect to restore state from localStorage on component mount
  useEffect(() => {
    const savedState = localStorage.getItem('pobDataState');
    if (savedState) {
      const state = JSON.parse(savedState);

      setSelectedState(state.selectedState);
      setSelectedDivision(state.selectedDivision);
      setSelectedCallType(state.selectedCallType || "ALL");
      setTraceDate(state.traceDate);
      setTableData(state.tableData);
      setShowContainers(true);

      if (state.selectedState && state.selectedState !== "ALL") {
        axios.get(`${BASE_URL}/api/divisions?state_id=${state.selectedState}`, {
          headers: getAuthHeaders()
        })
          .then(response => {
            setDivisions([{ id: "ALL", name: "ALL" }, ...response.data]);
          })
          .catch(err => console.error("Error restoring divisions:", err));
      } else {
        setDivisions([{ id: "ALL", name: "ALL" }]);
      }

      localStorage.removeItem('pobDataState');
    }
  }, []);

  // Fetch states on component mount
  useEffect(() => {
    const fetchStates = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/api/states`, {
          headers: getAuthHeaders()
        });
        setStates([{ id: "ALL", name: "ALL" }, ...response.data]);
      } catch (err) {
        console.error("Error fetching states:", err);
        setError("Failed to load states");
      }
    };
    fetchStates();
  }, []);

  // Fetch divisions when selectedState changes
  useEffect(() => {
    const fetchDivisions = async () => {
      if (selectedState && selectedState !== "ALL") {
        try {
          const response = await axios.get(
            `${BASE_URL}/api/divisions?state_id=${selectedState}`,
            { headers: getAuthHeaders() }
          );
          setDivisions([{ id: "ALL", name: "ALL" }, ...response.data]);
          setSelectedDivision("ALL");
        } catch (err) {
          console.error("Error fetching divisions:", err);
          setError("Failed to load divisions");
        }
      } else {
        setDivisions([{ id: "ALL", name: "ALL" }]);
        setSelectedDivision("ALL");
      }
    };
    fetchDivisions();
  }, [selectedState]);

  // Handle change for State dropdown
  const handleStateChange = (stateId) => {
    setSelectedState(stateId);
    setSelectedDivision("ALL");
    setSelectedCallType("ALL");
    setCurrentPage(1);
    setShowContainers(false);
  };

  // Handle change for Division dropdown
  const handleDivisionChange = (divisionId) => {
    setSelectedDivision(divisionId);
    setSelectedCallType("ALL");
    setCurrentPage(1);
  };

  // Handle search submit
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    setShowContainers(true);
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  // Handle items per page change
  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  // Main data fetching function
  const fetchData = useCallback(async () => {
    if (!showContainers && !(selectedState || selectedDivision || selectedCallType || traceDate)) {
      return;
    }

    setIsLoading(true);
    setError(null);
    try {
      let url = `${BASE_URL}/api/summary?`;

      // Add state_id if selected
      if (selectedState && selectedState !== "ALL") {
        url += `state_id=${selectedState}&`;
      }

      // Add division if selected
      if (selectedDivision && selectedDivision !== "ALL") {
        const division = divisions.find(d => String(d.id) === String(selectedDivision));
        if (division?.name) url += `division=${encodeURIComponent(division.name)}&`;
      }

      // Add call_type if selected
      if (selectedCallType && selectedCallType !== "ALL") {
        url += `call_type=${encodeURIComponent(selectedCallType)}&`;
      }

      // Add trace_date
      if (traceDate) {
        url += `trace_date=${traceDate}&`;
      }

      // Add search query if exists
      if (searchQuery) {
        url += `search=${encodeURIComponent(searchQuery)}&`;
      }

      // Add pagination parameters
      url += `page=${currentPage}&limit=${itemsPerPage}`;

      console.log("Fetching data from:", url); // Debug log

      const response = await axios.get(url, {
        headers: getAuthHeaders()
      });
      
      // Debug the response
      console.log("API Response:", response.data);

      // Handle different response formats
      if (response.data) {
        if (Array.isArray(response.data)) {
          // Handle case where API returns array directly
          setTableData(response.data);
          setTotalRecords(response.data.length);
        } else if (response.data.data && Array.isArray(response.data.data)) {
          // Handle paginated response
          setTableData(response.data.data);
          setTotalRecords(response.data.total_records || response.data.data.length);
        } else {
          // Handle other response formats
          setTableData([]);
          setTotalRecords(0);
        }
      } else {
        setTableData([]);
        setTotalRecords(0);
      }
      
      setShowContainers(true);
    } catch (err) {
      console.error("Failed to fetch summary", err);
      setError("Something went wrong while fetching data. Please try again.");
      setTableData([]);
      setTotalRecords(0);
    } finally {
      setIsLoading(false);
    }
  }, [
    selectedState, 
    selectedDivision, 
    selectedCallType, 
    traceDate, 
    searchQuery, 
    currentPage, 
    itemsPerPage, 
    divisions,
    showContainers
  ]);

  // Fetch data when filters or pagination changes
  useEffect(() => {
    if (showContainers) {
      fetchData();
    }
  }, [fetchData]);

  // Handle navigation to detail pages
  const handleNavigation = (path) => {
    const filterState = {
      selectedState,
      selectedDivision,
      selectedCallType,
      traceDate,
      tableData,
      divisions,
    };
    localStorage.setItem('pobDataState', JSON.stringify(filterState));
    navigate(path);
  };

  return (
    <div className="LocationHistorySMS-main-container">
      {isLoading && <Loader />}
      <div className={`main-container ${isLoading ? "blur-sm pointer-events-none" : ""}`}>
        <div className="LocationHistorySMS-filter-container">
          <h1 className="text-xl sm:text-2xl font-bold text-center text-gray-800 mb-4">
            <span className="text-blue-700"> Select </span>
            <span className="text-blue-500">Filter</span>
          </h1>
          <div className="LocationHistorySMS-filter-row1">
            <label className="LocationHistorySMS-label">State:</label>
            <select
              className="LocationHistorySMS-dropdown"
              value={selectedState}
              onChange={(e) => handleStateChange(e.target.value)}
              disabled={isLoading}
            >
              {states.map((state) => (
                <option key={state.id} value={state.id}>
                  {state.name}
                </option>
              ))}
            </select>

            <label className="LocationHistorySMS-label">Division:</label>
            <select
              className="LocationHistorySMS-dropdown"
              value={selectedDivision}
              onChange={(e) => handleDivisionChange(e.target.value)}
              disabled={!selectedState || isLoading}
            >
              {divisions.map((division) => (
                <option key={division.id} value={division.id}>
                  {division.name}
                </option>
              ))}
            </select>
          </div>

          <div className="LocationHistorySMS-filter-row2">
            <label className="LocationHistorySMS-label">Call Type:</label>
            <select
              className="LocationHistorySMS-dropdown"
              value={selectedCallType}
              onChange={(e) => {
                setSelectedCallType(e.target.value);
                setCurrentPage(1);
                setShowContainers(true);
              }}
              disabled={isLoading}
            >
              <option value="ALL">ALL</option>
              <option value="No Reporting">No Reporting</option>
              <option value="Reporting Only">Reporting Only</option>
              <option value="1<=Calls<5">1&lt;=Calls&lt;5</option>
              <option value="5<=Calls<10">5&lt;=Calls&lt;10</option>
              <option value="10<=Calls<20">10&lt;=Calls&lt;20</option>
              <option value="Calls>=20">Calls&gt;=20</option>
            </select>

            <label className="LocationHistorySMS-label">Trace Date:</label>
            <input
              type="date"
              className="LocationHistorySMS-dropdown"
              value={traceDate}
              onChange={(e) => {
                setTraceDate(e.target.value);
                setCurrentPage(1);
                setShowContainers(true);
              }}
              disabled={isLoading}
            />
          </div>

          <button
            className="LocationHistorySMS-go-button"
            onClick={() => {
              setCurrentPage(1);
              setShowContainers(true);
            }}
            disabled={isLoading || !selectedState}
          >
            {isLoading ? "Loading..." : "GO"}
          </button>

          {error && (
            <div className="LocationHistorySMS-error">
              {error}
              <button onClick={fetchData} className="ml-2 text-blue-600">Retry</button>
            </div>
          )}
        </div>

        {showContainers && (
          <>
            <div className="LocationHistorySMS-table-container">
              <div className="LocationHistorySMS-title3-container">
                <h2 className="LocationHistorySMS-title3">
                  POB Data -(
                  {selectedState && `State: ${states.find(s => String(s.id) === String(selectedState))?.name || 'ALL'}`}
                  {" | Division: "}
                  {selectedDivision === "ALL"
                    ? "ALL Divisions"
                    : divisions.find(d => String(d.id) === String(selectedDivision))?.name || "Unknown Division"}
                  {" | Call Type: "}
                  {selectedCallType}
                  {" | Working Date: "}
                  {formatDateForDisplay(traceDate)}
                  )
                </h2>
              </div>

              <div className="relative mb-4 flex justify-end">
                <form
                  onSubmit={handleSearchSubmit}
                  className="flex items-center gap-2"
                >
                  <input
                    ref={inputRef}
                    type="text"
                    placeholder="Search employees..."
                    className={`transition-all duration-300 ease-in-out border border-gray-300 rounded-full px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                      isSearchExpanded || searchQuery
                        ? "w-64 opacity-100"
                        : "w-0 opacity-0 px-0 py-0"
                    }`}
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      if (e.target.value === "") {
                        setCurrentPage(1);
                      }
                    }}
                    onBlur={() => !searchQuery && setIsSearchExpanded(false)}
                  />
                  <button
                    type="button"
                    onClick={handleExpandSearch}
                    className="p-2 text-gray-500 hover:text-gray-700 focus:outline-none"
                  >
                    <FaSearch className="w-5 h-5" />
                  </button>
                </form>
              </div>

              {tableData.length === 0 && !isLoading && (
                <div className="text-center py-4 text-gray-500">
                  No records found. Please try different filters.
                </div>
              )}

              {tableData.length > 0 && (
                <>
                  <div className="LocationHistorySMS-table-wrapper">
                    <table className="LocationHistorySMS-table">
                      <thead className="text-white">
                        <tr>
                          <th>S.No</th>
                          <th>Date</th>
                          <th>Employee</th>
                          <th>Emp Code</th>
                          <th>State</th>
                          <th>City</th>
                          <th>Division</th>
                          <th>Leave</th>
                          <th>Leave Remarks</th>
                          <th>
                            <div className="call-data-container">
                              <span>Web |</span>
                              <span>Mob |</span>
                              <span>Calls </span>
                            </div>
                          </th>
                          <th>POB</th>
                          <th>Location</th>
                          <th>MAP</th>
                          <th>SMS</th>
                          <th>DCR Remarks</th>
                          <th>Latest Location</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tableData.map((row, index) => (
                          <tr key={`${row.employee_id}-${index}`}>
                            <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                            <td>{formatDateForDisplay(traceDate)}</td>
                            <td>{row.employee_name}</td>
                            <td>{row.employee_id}</td>
                            <td>{row.state}</td>
                            <td>{row.city}</td>
                            <td>{row.division}</td>
                            <td>{row.leave}</td>
                            <td>{row.leaveremarks}</td>
                            <td>
                              <div className="call-data-container">
                                <div className="call-data-box call-web">{row.web}</div>|
                                <div className="call-data-box call-mob">{row.mobile}</div>|
                                <div className="call-data-box call-calls">{row.calls}</div>
                              </div>
                            </td>
                            <td className="LocationHistorySMS-clickable"
                              onClick={() => handleNavigation(`/doctordetails/${row.employee_id}/doctor`)}>
                              {row.pob}
                            </td>
                            <td className="LocationHistorySMS-clickable"
                              onClick={() => handleNavigation(`/chemistdetails/${row.employee_id}`)}>{row.location}</td>
                            <td className="LocationHistorySMS-clickable"
                              onClick={() => handleNavigation(`/stockistdetails/${row.employee_id}`)}>{row.map}</td>
                            <td>{row.mobile}</td>
                            <td className="LocationHistorySMS-clickable"
                              onClick={() => handleNavigation(`/pob-details-doctor-chemist/${row.employee_id}`)}>{row.dcr_remarks}</td>
                            <td className="LocationHistorySMS-clickable"
                              onClick={() => handleNavigation(`/pob-details-doctor-chemist-datewise/${row.employee_id}`)}>{row.latest_location}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  <div className="flex flex-col sm:flex-row items-center justify-between px-4 py-3 bg-gray-100 border-t border-gray-300 mt-4">
                    <div className="flex items-center mb-2 sm:mb-0">
                      <label
                        htmlFor="itemsPerPage"
                        className="mr-2 font-medium text-sm text-gray-700"
                      >
                        View
                      </label>
                      <select
                        id="itemsPerPage"
                        className="block w-20 rounded-md border border-gray-300 bg-white px-2 py-1 text-sm shadow-sm focus:border-indigo-500 focus:outline-none"
                        value={itemsPerPage}
                        onChange={handleItemsPerPageChange}
                      >
                        <option value="10">10</option>
                        <option value="20">20</option>
                        <option value="30">30</option>
                        <option value="50">50</option>
                      </select>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className="text-sm text-gray-600 font-medium">
                        {tableData.length > 0 
                          ? `${(currentPage - 1) * itemsPerPage + 1}-${Math.min(currentPage * itemsPerPage, totalRecords)} of ${totalRecords}`
                          : "0-0 of 0"
                        }
                      </span>
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1 || tableData.length === 0}
                        className="px-2 py-1 rounded border text-gray-600 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        ←
                      </button>
                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage * itemsPerPage >= totalRecords || tableData.length === 0}
                        className="px-2 py-1 rounded border text-gray-600 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        →
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default LocationHistorySMS;